import { Dialog } from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Button } from "../ui/Button.jsx";
import { Input } from "../ui/Input.jsx";
import { Label } from "../ui/Label.jsx";
import { updateTeamMember } from "../../services/teamService.js";
import { useToast } from "../../hooks/useToast.js";

export function EditTeamMemberModal({
  isOpen,
  setIsOpen,
  member,
  onUpdated,
  startEditable = false,
}) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    email: "",
    contact_number: "",
    user_role: "",
  });
  const [errors, setErrors] = useState({});
  const [isEditable, setIsEditable] = useState(startEditable);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setIsEditable(startEditable);
  }, [startEditable, isOpen]);

  useEffect(() => {
    if (member) {
      setFormData({
        id: member.id,
        name: member.name || "",
        email: member.email || "",
        contact_number: member.contact_number || "",
        user_role: member.user_role || "",
      });
      setErrors({});
    }
  }, [member]);

  const handleChange = (e) => {
    if (!isEditable) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    else if (formData.name.trim().length < 2)
      newErrors.name = "Name must be at least 2 characters";

    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email format";

    if (formData.contact_number.trim()) {
      if (!/^\d{10}$/.test(formData.contact_number.trim()))
        newErrors.contact_number = "Phone number must be 10 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditable) {
      setIsEditable(true);
      return;
    }
    if (!validateForm()) return;

    setLoading(true);
    try {
      const updated = await updateTeamMember(formData.id, formData);
      toast({
        title: "Updated successfully",
        description: `${updated.name || formData.name} has been updated.`,
      });
      onUpdated?.(updated);
      setIsOpen(false);
    } catch (err) {
      toast({
        title: "Update failed",
        description: err?.message || "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog
          static
          open={isOpen}
          onClose={() => setIsOpen(false)}
          className="fixed inset-0 z-50 flex flex-col justify-end"
        >
          {/* Overlay Fade */}
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-xs"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setIsOpen(false)}
          />

          {/* Bottom Sheet Modal */}
          <motion.div
            key="edit-team-member-modal"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{
              type: "spring",
              stiffness: 180,
              damping: 22,
              mass: 0.5,
            }}
            className="relative w-full bg-white rounded-t-3xl shadow-lg p-5 z-50"
          >
            {/* Handle Bar */}
            <div
              className="bg-gray-400/70 absolute top-1 left-1/2 -translate-x-1/2 h-1 w-16 rounded-full"
              onClick={() => setIsOpen(false)}
            ></div>

            <Dialog.Title className="text-base font-semibold text-gray-800 mb-4 text-center">
              Edit Team Member
            </Dialog.Title>

            <form onSubmit={handleSubmit} className="space-y-3 mb-4">
              {/* Name */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Name
                </Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={!isEditable}
                  placeholder="Full name"
                  className={`h-8 text-xs border-blue-100/50 ${
                    errors.name ? "border-red-500" : ""
                  }`}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Email
                </Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!isEditable}
                  placeholder="Email address"
                  className={`h-8 text-xs border-blue-100/50 ${
                    errors.email ? "border-red-500" : ""
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Contact Number
                </Label>
                <Input
                  name="contact_number"
                  value={formData.contact_number}
                  onChange={handleChange}
                  disabled={!isEditable}
                  placeholder="Phone number"
                  className={`h-8 text-xs border-blue-100/50 ${
                    errors.contact_number ? "border-red-500" : ""
                  }`}
                />
                {errors.contact_number && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.contact_number}
                  </p>
                )}
              </div>

              {/* Role */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  User Role
                </Label>
                <Input
                  value={String(formData.user_role).split("_").join(" ")}
                  disabled
                  className="h-8 text-xs bg-gray-100 text-gray-600 cursor-not-allowed border-blue-100/50"
                />
              </div>
            </form>

            {/* Footer Buttons */}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsOpen(false)}
                className="h-8 px-3 text-xs rounded-full border border-gray-200 text-gray-600"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                onClick={handleSubmit}
                disabled={loading}
                className="h-8 px-4 text-xs rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391] text-white border"
              >
                {loading ? "Saving..." : isEditable ? "Save" : "Edit"}
              </Button>
            </div>
          </motion.div>
        </Dialog>
      )}
    </AnimatePresence>
  );
}
